import type { Id } from "@convex/_generated/dataModel";
/**
 * Portal Employee Permissions Hook
 *
 * Provides the current auth user's employee record and permissions.
 * Uses the employee's userId (set when they accept org invitation) to
 * look up their employee record and aggregate permissions from all roles.
 */
import type { EmployeePermissions, PermissionKey } from "@convex/permissions";
import { useConvexAuth, useQuery } from "convex/react";
import { createContext, use } from "react";

import { employeesGetEmployeeByUserId } from "~/api-refs";

export type { EmployeePermissions, PermissionKey } from "@convex/permissions";

export interface PortalEmployee {
  _id: Id<"employees">;
  _creationTime: number;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  imageUrl?: string;
  hourlyRate?: number;
  pin: string;
  disabled: boolean;
  organizationId: Id<"organizations">;
  userId?: string;
  addToFutureLocations: boolean;
  createdAt?: number;
  updatedAt: number;
  roleNames: string[];
}

export interface EmployeePermissionsData {
  /** The employee record, or null if user has no linked employee */
  employee: PortalEmployee | null;
  /** The aggregated permissions from all roles, or null if no employee */
  permissions: EmployeePermissions | null;
  /** Actual role names from the employee's assigned roles */
  roleNames: string[];
  /** Whether the query is still loading */
  isLoading: boolean;
  /** Whether the user has a linked employee record */
  hasEmployee: boolean;
  /** Check if user has a specific permission */
  hasPermission: (permission: PermissionKey) => boolean;
}

const EmployeePermissionsContext =
  createContext<EmployeePermissionsData | null>(null);

/**
 * Hook to access the current user's employee permissions.
 * Must be used within EmployeePermissionsProvider.
 */
export function useEmployeePermissions(): EmployeePermissionsData {
  const context = use(EmployeePermissionsContext);
  if (!context) {
    throw new Error(
      "useEmployeePermissions must be used within EmployeePermissionsProvider"
    );
  }
  return context;
}

/**
 * Hook that fetches and provides employee permissions data.
 * Use this at the provider level.
 */
export function useEmployeePermissionsQuery(): EmployeePermissionsData {
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();

  // Skip query until authenticated to avoid "Authentication required" errors
  const result = useQuery(
    employeesGetEmployeeByUserId,
    isAuthenticated ? {} : "skip"
  );

  const employee = result?.employee ?? null;
  const permissions = result?.permissions ?? null;
  const roleNames = employee?.roleNames ?? [];
  const isLoading = isAuthLoading || result === undefined;
  const hasEmployee = !!employee;

  const hasPermission = (permission: PermissionKey): boolean => {
    if (!permissions) {
      return false;
    }
    return permissions[permission];
  };

  return {
    employee,
    permissions,
    roleNames,
    isLoading,
    hasEmployee,
    hasPermission,
  };
}

export { EmployeePermissionsContext };
